import { useCallback, useEffect, useRef, useState } from 'react';
import { listConversations } from '../api/adminApi.js';
import type { AdminConversation, ListConversationsOptions, ListConversationsPagination } from '../api/types.js';

const SEARCH_DEBOUNCE_MS = 300;
const CONVERSATION_PAGE_SIZE = 100;
const CONVERSATION_REQUEST_TIMEOUT_MS = 10_000;

export type ConversationScope = 'whitelist' | 'all' | 'manual';

export interface UseConversationsResult {
  conversations: AdminConversation[];
  loading: boolean;
  error: string | null;
  searchTerm: string;
  debouncedSearchTerm: string;
  scope: ConversationScope;
  pagination: ListConversationsPagination;
  setSearchTerm: (value: string) => void;
  setScope: (value: ConversationScope) => void;
  goToPreviousPage: () => Promise<void>;
  goToNextPage: () => Promise<void>;
  refresh: (options?: { background?: boolean; toast?: (msg: string) => void }) => Promise<void>;
  upsertConversation: (conversation: AdminConversation) => void;
  clear: () => void;
}

const EMPTY_PAGINATION: ListConversationsPagination = {
  limit: CONVERSATION_PAGE_SIZE,
  offset: 0,
  count: 0,
  hasPrevious: false,
  hasNext: false,
  nextOffset: null,
  previousOffset: null,
};

export function useConversations({ enabled = true }: { enabled?: boolean } = {}): UseConversationsResult {
  const [conversations, setConversations] = useState<AdminConversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [scope, setScope] = useState<ConversationScope>('whitelist');
  const [offset, setOffset] = useState(0);
  const [pagination, setPagination] = useState<ListConversationsPagination>(EMPTY_PAGINATION);
  const mounted = useRef(true);
  const requestSeq = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  const doFetch = useCallback(
    async (options: { background?: boolean; toast?: (msg: string) => void } = {}) => {
      if (!enabled) return;
      const seq = requestSeq.current + 1;
      requestSeq.current = seq;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      let timedOut = false;
      const timeout = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, CONVERSATION_REQUEST_TIMEOUT_MS);
      const showLoading = !options.background;
      if (showLoading) setLoading(true);
      setError(null);
      try {
        const opts: ListConversationsOptions = {
          q: debouncedSearchTerm || undefined,
          whitelist: debouncedSearchTerm ? false : scope === 'whitelist',
          mode: scope === 'manual' ? 'manual' : undefined,
          days: scope === 'whitelist' ? 7 : undefined,
          limit: CONVERSATION_PAGE_SIZE,
          offset,
        };
        const data = await listConversations(opts, { signal: controller.signal });
        if (mounted.current && requestSeq.current === seq) {
          setConversations(data.conversations || []);
          setPagination(data.pagination ?? {
            ...EMPTY_PAGINATION,
            offset,
            count: data.conversations?.length ?? 0,
            hasPrevious: offset > 0,
            previousOffset: offset > 0 ? Math.max(0, offset - CONVERSATION_PAGE_SIZE) : null,
          });
          if (options.toast) options.toast('已刷新会话');
        }
      } catch (err) {
        if (isAbortError(err)) {
          if (timedOut && mounted.current && requestSeq.current === seq) {
            setError('加载会话超时，请刷新页面或检查后端连接');
          }
          return;
        }
        if (mounted.current && requestSeq.current === seq) {
          setError(err instanceof Error ? err.message : '加载会话失败');
        }
      } finally {
        window.clearTimeout(timeout);
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
        if (showLoading && mounted.current && requestSeq.current === seq) setLoading(false);
      }
    },
    [debouncedSearchTerm, enabled, offset, scope],
  );

  const refresh = useCallback(
    async (options: { background?: boolean; toast?: (msg: string) => void } = {}) => {
      await doFetch(options);
    },
    [doFetch],
  );

  const updateSearchTerm = useCallback((value: string) => {
    setOffset(0);
    setSearchTerm(value);
  }, []);

  const updateScope = useCallback((value: ConversationScope) => {
    setOffset(0);
    setScope(value);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    doFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, debouncedSearchTerm, offset, scope]);

  const goToPreviousPage = useCallback(() => {
    setOffset(pagination.previousOffset ?? 0);
    return Promise.resolve();
  }, [pagination.previousOffset]);

  const goToNextPage = useCallback(() => {
    if (pagination.nextOffset === null) return Promise.resolve();
    setOffset(pagination.nextOffset);
    return Promise.resolve();
  }, [pagination.nextOffset]);

  const upsertConversation = useCallback((conversation: AdminConversation) => {
    setConversations((prev) => {
      const index = prev.findIndex((item) => item.friendId === conversation.friendId);
      if (index >= 0) {
        const next = [...prev];
        next[index] = { ...next[index], ...conversation };
        return next;
      }
      return [conversation, ...prev];
    });
  }, []);

  const clear = useCallback(() => {
    abortRef.current?.abort();
    requestSeq.current += 1;
    setConversations([]);
    setError(null);
    setLoading(false);
    setOffset(0);
    setPagination(EMPTY_PAGINATION);
  }, []);

  return {
    conversations,
    loading,
    error,
    searchTerm,
    debouncedSearchTerm,
    scope,
    pagination,
    setSearchTerm: updateSearchTerm,
    setScope: updateScope,
    goToPreviousPage,
    goToNextPage,
    refresh,
    upsertConversation,
    clear,
  };
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
    || error instanceof Error && error.name === 'AbortError';
}
